import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Form } from "@ps-design-system/components/Form";
import { Text } from "@ps-design-system/components/Text";

import type { Space } from "../../../../../types/spaces.types";
import { trackFormNavClicked } from "../../utils/your-space.utils";
import { useArrivalGuideForm } from "../useArrivalGuideForm";

import { DirectionsField } from "./form-fields/DirectionsField";
import { WifiInstructionsField } from "./form-fields/WifiInstructionsField";

type ArrivalGuideFormProps = {
  space: Space;
};

/**
 * The form component for editing a space's arrival guide: wifi instructions
 * and directions. Uses `useArrivalGuideForm` to manage form state and
 * handle submission.
 */
const ArrivalGuideForm = ({ space }: ArrivalGuideFormProps) => {
  const { form, onSave, errorMsg } = useArrivalGuideForm(space);

  const handleSave = async () => {
    const success = await onSave();
    if (success) {
      trackFormNavClicked("next");
      window.location.href =
        space.status === "DRAFT"
          ? `/your-space/${space._id}/photos`
          : `/your-space/${space._id}`;
    }
  };

  const handleBack = () => trackFormNavClicked("previous");

  return (
    <Form {...form} spacing={0}>
      <WifiInstructionsField />

      <Divider spacing={48} />

      <DirectionsField />

      <Divider spacing={48} />

      <div className="tw-flex tw-justify-between tw-mt-32 tw-items-start">
        <Button
          appearance="white"
          variant="outline-1"
          href={`/your-space/${space._id}`}
          onClick={handleBack}
        >
          <FormattedMessage
            id="hosting.arrival_guide.back_button"
            defaultMessage="Back"
          />
        </Button>

        <div className="tw-flex tw-flex-col tw-items-end tw-gap-8">
          <Button
            appearance="purple"
            disabled={!form.formState.isDirty}
            onClick={handleSave}
            showSpinner={form.formState.isSubmitting}
            testId="arrival-guide-save-button"
          >
            <FormattedMessage
              id="hosting.arrival_guide.save_button"
              defaultMessage="Save"
            />
          </Button>
          {errorMsg && (
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-red-800"
              testId="arrival-guide-submit-error"
            >
              {errorMsg}
            </Text>
          )}
        </div>
      </div>
    </Form>
  );
};

export { ArrivalGuideForm };
